import { useEffect, useRef, useState } from 'react';
import type { ConversationItem } from '@chatapp-inbox/shared';
import { MAX_TIMEOUT_MS, deadlineOf, windowStatus } from './reply-window';
import { useWindowRefusal, type WindowRefusal } from './reply-window-refusal';

export interface ReplyWindowOptions {
  /**
   * Intervalo (ms) em que a tela se atualiza ENQUANTO a janela está aberta e a aba visível (a
   * contagem regressiva, o "faltam X"). Sem ele só os prazos mexem na tela.
   */
  tickMs?: number;
  /** O intervalo só roda quando faltar tanto (ms) ou menos para fechar. Padrão: durante toda a janela. */
  tickWithinMs?: number;
}

/** O que a tela decide da janela de resposta agora (ver `windowStatus`), já com a palavra do servidor. */
export type ReplyWindowState =
  | { kind: 'none' }
  | { kind: 'open'; expiresAt: number; remainingMs: number; now: number }
  | {
      kind: 'closed';
      expiresAt: number | null;
      /** O servidor recusou um envio nesta janela (mesmo que o relógio local ainda a visse aberta). */
      refusal: WindowRefusal | null;
    };

/**
 * Decide aberta/fechada da janela de resposta pelo RELÓGIO LOCAL, sem pedir nada ao servidor:
 *
 * - um `setTimeout` exato para o prazo (e para o início da contagem, se `tickWithinMs`) vira a tela
 *   quando a janela fecha, e é refeito a cada vez que o prazo vem novo do servidor (o cliente
 *   escreveu: `conversation.updated`). Acima do teto do `setTimeout` (~24,8 dias) o temporizador
 *   dorme o teto e confere de novo;
 * - abas em segundo plano atrasam temporizadores (e o computador pode ter dormido): ao voltar à aba
 *   ou à janela, o relógio é relido;
 * - o intervalo de `tickMs` só roda com a aba visível.
 *
 * Quem decide é o servidor: uma recusa `409 reply_window_closed` (ver `recordWindowRefusal`)
 * fecha a janela na tela mesmo que o relógio local (atrasado) a visse aberta, até o prazo mudar.
 */
export function useReplyWindow(
  conversation: Pick<ConversationItem, 'id' | 'replyWindow'>,
  { tickMs, tickWithinMs = Infinity }: ReplyWindowOptions = {},
): ReplyWindowState {
  const { id, replyWindow } = conversation;
  const [now, setNow] = useState(() => Date.now());
  const deadline = deadlineOf(replyWindow);
  const status = windowStatus(replyWindow, now);
  const lastDeadline = useRef<number | null | undefined>(undefined);

  // Prazos exatos: o início da contagem e o fechamento. Depois de cada disparo confere a hora de
  // novo (um temporizador pode disparar alguns ms antes) e arma o próximo.
  useEffect(() => {
    if (deadline === null) return;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const arm = (): void => {
      const current = Date.now();
      const upcoming = [deadline - tickWithinMs, deadline].filter((moment) => moment > current);
      if (upcoming.length === 0) return;
      timer = setTimeout(
        () => {
          setNow(Date.now());
          arm();
        },
        Math.min(Math.min(...upcoming) - current, MAX_TIMEOUT_MS),
      );
    };
    arm();
    return () => clearTimeout(timer);
  }, [deadline, tickWithinMs]);

  // O relógio guardado pode estar velho (só anda nos prazos e nas atualizações): quando o prazo MUDA
  // (o cliente escreveu) ele é relido logo em seguida, para o "faltam 24 h" não nascer com minutos
  // a mais. Na montagem o relógio acabou de ser lido: nada a refazer.
  useEffect(() => {
    const changed = lastDeadline.current !== undefined && lastDeadline.current !== deadline;
    lastDeadline.current = deadline;
    if (!changed) return;
    const reread = setTimeout(() => setNow(Date.now()), 0);
    return () => clearTimeout(reread);
  }, [deadline]);

  const open = status.kind === 'open';
  const ticking = tickMs !== undefined && open && status.remainingMs <= tickWithinMs;

  // Atualização periódica (contagem), só com a aba visível: escondida, para; ao voltar, relê e recomeça.
  useEffect(() => {
    if (!ticking) return;
    let timer: ReturnType<typeof setInterval> | undefined;
    const stop = (): void => {
      clearInterval(timer);
      timer = undefined;
    };
    const sync = (): void => {
      stop();
      if (document.visibilityState === 'visible') {
        timer = setInterval(() => setNow(Date.now()), tickMs);
      }
    };
    sync();
    document.addEventListener('visibilitychange', sync);
    return () => {
      stop();
      document.removeEventListener('visibilitychange', sync);
    };
  }, [ticking, tickMs]);

  // Voltar à aba/janela: os temporizadores podem ter atrasado (aba em segundo plano, máquina
  // suspensa), então o relógio é relido na hora.
  useEffect(() => {
    if (!open) return;
    const reread = (): void => {
      if (document.visibilityState === 'visible') setNow(Date.now());
    };
    document.addEventListener('visibilitychange', reread);
    window.addEventListener('focus', reread);
    return () => {
      document.removeEventListener('visibilitychange', reread);
      window.removeEventListener('focus', reread);
    };
  }, [open]);

  // A recusa do servidor só vale para a tela que a presenciou e até o prazo AUMENTAR em relação ao de
  // quando ela chegou (o cliente escreveu: a janela reabriu, e o relógio local volta a decidir). Um
  // prazo igual ou MENOR (a conversa refeita pelo servidor traz o prazo verdadeiro, que pode ser
  // anterior ao que a tela tinha) e "nunca escreveu" continuam fechados.
  const refusal = useWindowRefusal(id);
  const [mountedAt] = useState(() => Date.now());
  const [seen, setSeen] = useState<{ at: number; deadline: number | null } | null>(null);
  if (refusal !== null && refusal.at >= mountedAt && seen?.at !== refusal.at) {
    setSeen({ at: refusal.at, deadline });
  }
  const refused =
    refusal !== null &&
    seen?.at === refusal.at &&
    (deadline ?? -Infinity) <= (seen.deadline ?? -Infinity)
      ? refusal
      : null;

  if (status.kind === 'none') return status;
  if (status.kind === 'closed') return { ...status, refusal: refused };
  return refused ? { kind: 'closed', expiresAt: status.expiresAt, refusal: refused } : status;
}
